import InstallmentsModel, { recreateCode } from "../models/InstallmentsModel";
import { IInstallment } from "../interfaces";
import { decimal } from "../../utils/DecimalPrecision";

import {
  GreaterThanException,
  GreaterThanOrEqualException,
  BetweenValuesException,
  MustBeIntegerException,
} from "../exceptions/InvalidValueException";

const maxQuantity = 50;

class InstallmentsService {
  recalculateInstallments(
    installmentsObj: InstallmentsModel,
    newQuantity: number,
    newTotal: number,
    newDate?: Date
  ): IInstallment[] {
    newTotal = decimal(newTotal)
      .subtract(installmentsObj.totalLinked)
      .toNumber();
    newQuantity -= installmentsObj.quantityLinked;

    if (newTotal < 0 || newQuantity < 0) {
      return installmentsObj.installments;
    }

    const installmentsLinked = installmentsObj.installmentsLinked;
    const newIntallments = this.generateInstallments(
      getDateRef(),
      newQuantity,
      newTotal
    );
    return recreateCode([...installmentsLinked, ...newIntallments]);

    function getDateRef() {
      return installmentsObj.quantityLinked
        ? installmentsObj.getLastMaturityDate()
        : newDate || installmentsObj.dateRef;
    }
  }

  generateInstallments(
    date: Date,
    quantity: number,
    total: number
  ): IInstallment[] {
    const dateRef = new Date(date);

    if (total > 0) {
      if (this.quantityIsValid(quantity) && dateRef) {
        if (!Number.isInteger(quantity))
          throw new MustBeIntegerException(quantity, "quantity");

        var value = decimal(total, 2).dividedBy(quantity).round();
        const newInstallments = addInstallments(value, quantity);
        adjustInstallmentsDiff(newInstallments, value, quantity);

        return newInstallments.filter((installment) => installment.value > 0);
      }
    }
    return [];

    function addInstallments(value, quantity) {
      const newInstallments = new Array<IInstallment>();
      for (let i = 0; i < quantity; i++)
        newInstallments.push({
          id: i + 1,
          value,
          maturityDate: addOneMonth(dateRef),
        });
      return newInstallments;
    }

    function addOneMonth(dateRef) {
      dateRef.setMonth(dateRef.getMonth() + 1);
      return new Date(dateRef);
    }

    function adjustInstallmentsDiff(installments, value, quantity, i = 0) {
      const currentTotal = decimal(value).times(quantity).toNumber();
      const diff = decimal(total).subtract(currentTotal).toNumber();

      if (diff === 0 || quantity < 1) return;

      const newFirstInstallment = decimal(installments[i].value, 2)
        .sum(diff)
        .round();
      installments[i].value = newFirstInstallment;

      if (diff < 0 && diff * -1 > value) {
        adjustInstallmentsDiff(installments, value, quantity - 1, i + 1);
      }
    }
  }

  validateData(data: InstallmentsModel): Boolean {
    if (!data.total || data.total <= 0)
      throw new GreaterThanException(data.total, 0, "total");

    if (!data.quantity || !this.quantityIsValid(data.quantity))
      throw new BetweenValuesException(
        data.quantity,
        1,
        maxQuantity,
        "quantity"
      );

    return true;
  }

  private quantityIsValid(quantity) {
    return quantity > 0 && quantity <= maxQuantity;
  }

  validateUpdatedData(
    data: InstallmentsModel,
    updatedData: InstallmentsModel
  ): Boolean {
    if (data.totalLinked > updatedData.total) {
      throw new GreaterThanOrEqualException(
        updatedData.total,
        data.totalLinked,
        "total"
      );
    }

    if (data.quantityLinked > updatedData.quantity) {
      throw new GreaterThanOrEqualException(
        updatedData.quantity,
        data.quantityLinked,
        "quantity"
      );
    }

    return true;
  }
}

export default new InstallmentsService();
